import React, { useEffect, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { ArrowLeft, MoreHorizontal } from 'lucide-react';
import { aaiApi } from '../../../services/aaiApi';
import type { ConnectorCatalog, ConnectorRow } from '../../../types/aai';
import { Action } from '../../aa';
import { ManageInWorkspace, workspaceGitLine } from '../ManageInWorkspace';
import { Mark, authOf, type PanelMode, type RowHandlers } from './ConnectorsTable';
import { ToolPermissions } from './ToolPermissions';

const detailOf = (err: unknown, fallback: string): string =>
  (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || fallback;

interface MenuItem { label: string; danger?: boolean; run: () => void }

const Menu: React.FC<{ items: MenuItem[] }> = ({ items }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    window.addEventListener('mousedown', onDown);
    return () => window.removeEventListener('mousedown', onDown);
  }, [open]);
  if (items.length === 0) return null;
  return (
    <div ref={ref} className="relative">
      <button type="button" aria-label="More" onClick={() => setOpen((v) => !v)} className="grid h-10 w-10 place-items-center rounded-act text-ink-muted hover:bg-canvas-inset hover:text-ink">
        <MoreHorizontal size={18} />
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 min-w-[220px] rounded-card border border-line bg-white py-1 shadow-lg">
          {items.map((it) => (
            <button
              key={it.label}
              type="button"
              onClick={() => { setOpen(false); it.run(); }}
              className={`block w-full px-3.5 py-2 text-left text-[13px] hover:bg-canvas-inset ${it.danger ? 'text-danger' : 'text-ink'}`}
            >
              {it.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const subline = (row: ConnectorRow): string => {
  if (!row.enabled) return `${row.category_label} · Off for everyone at your company`;
  if (!row.connect_here) return workspaceGitLine(row);
  if (authOf(row) === 'Individual') return `${row.category_label} · Connected with your own account`;
  if (authOf(row) === 'Company') return `${row.category_label} · Connected with the company account`;
  return `${row.category_label} · Not connected yet`;
};

export const ConnectorDetail: React.FC<{
  row: ConnectorRow;
  catalog: ConnectorCatalog;
  mode: PanelMode;
  handlers: RowHandlers;
  onBack: () => void;
  onChanged: () => void;
}> = ({ row, catalog, mode, handlers, onBack, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const label = row.label;

  const run = async (work: () => Promise<unknown>, done: string, fail: string) => {
    setBusy(true);
    try {
      await work();
      toast.success(done);
      onChanged();
    } catch (err) {
      toast.error(detailOf(err, fail));
    } finally {
      setBusy(false);
    }
  };

  const disconnect = (row: ConnectorRow) => {
    if (!window.confirm(`Disconnect your ${row.label}? Anything relying on it stops working until it is reconnected.`)) return;
    void run(() => aaiApi.revokeConnector(row.slug, 'personal'), `${row.label} is disconnected.`, `Could not disconnect ${row.label}.`);
  };

  const primary = (): React.ReactNode => {
    if (!row.enabled) return null;
    if (!row.connect_here && row.workspace_git?.connected) return <ManageInWorkspace row={row} label="Manage in Workspace" />;
    if (!row.connect_here) return <ManageInWorkspace row={row} label="Connect" />;
    if (row.personal_status === 'connected') return <Action variant="quiet" disabled={busy} reason="Working" onClick={() => disconnect(row)}>Disconnect</Action>;
    if (!catalog.can_use) return null;
    return <Action disabled={handlers.busy === row.slug} reason="Connecting" onClick={() => handlers.onConnect(row)}>Connect</Action>;
  };

  const menu = (): MenuItem[] => {
    const items: MenuItem[] = [];
    return items;
  };

  // Everyone, the Owner included, sets only their own permissions.
  const permissionMode = 'personal';

  return (
    <div className="mx-auto max-w-[1000px]">
      <Action size="sm" variant="ghost" icon={<ArrowLeft size={14} />} onClick={onBack}>Connectors</Action>
      <div className="mt-4 flex flex-wrap items-center justify-between gap-3">
        <span className="flex min-w-0 items-center gap-3">
          <Mark row={row} size="xl" />
          <h2 className="m-0 truncate text-[24px] font-bold leading-[31px] tracking-[-0.015em] text-ink">{label}</h2>
        </span>
        <span className="flex items-center gap-2">
          {primary()}
          <Menu items={menu()} />
        </span>
      </div>
      <p className="m-0 mt-2 text-[13.5px] leading-[21px] text-ink-muted">{subline(row)}</p>

      <ToolPermissions tool={row.slug} label={label} mode={permissionMode} />
    </div>
  );
};
